import _ from 'lodash';
import React, { FC } from 'react';
import { Box, Text, Tag, Heading } from '@wix/design-system';
import {
  Code,
  Database,
  CodeSmall,
  DatabaseSmall,
} from '@wix/wix-ui-icons-common';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { useTemplateContext } from '../../Contexts/TemplateContext';
import { APP_NAMES } from '@services/TemplateListingUtils';
import { Template } from '@common/types';

type FeaturesProps = {
  isMobileView: boolean;
};

const Features: FC<FeaturesProps> = ({ isMobileView }) => {
  const { t } = useTranslation();
  const { template } = useTemplateContext();

  const featuresTagsIdToIcon = (tag: string) => {
    switch (tag) {
      case _.toLower(APP_NAMES.USER_CODE):
        return isMobileView ? <CodeSmall /> : <Code />;
      case _.toLower(APP_NAMES.CMS):
        return isMobileView ? <DatabaseSmall /> : <Database />;
      default:
        return undefined;
    }
  };

  const renderTags = (tags?: Template['apps']) => {
    return tags?.map((tag, index) => {
      const { name, isKey } = tag;
      const displayName = isKey
        ? t(`marketplace.templates-page.feature-${name}`)
        : name;
      const dataHook = `template-features-tag-${name}`;

      return (
        <Box key={name} dataHook={`${name}-${index}`}>
          <Tag
            id={name}
            removable={false}
            dataHook={dataHook}
            theme="neutral"
            size={isMobileView ? 'small' : 'large'}
            thumb={featuresTagsIdToIcon(name)}
          >
            {displayName}
          </Tag>
        </Box>
      );
    });
  };

  const renderHeader = () =>
    isMobileView ? (
      <>
        <Box paddingBottom="6px">
          <Text size="small" secondary>
            {t('marketplace.template-page.features-title-mobile')}
          </Text>
        </Box>
      </>
    ) : (
      <Box paddingBottom="18px" dataHook="template-features-title">
        <Heading size="large" as="h2">
          {t('marketplace.template-page.features-title')}
        </Heading>
      </Box>
    );

  return (
    <Box
      direction="vertical"
      dataHook="features-container"
      paddingBottom={isMobileView ? '24px' : '0px'}
    >
      {renderHeader()}
      <Box display="flex" flexWrap="wrap" gap="12px">
        {renderTags(template?.apps)}
      </Box>
      {!isMobileView && (
        <Box paddingTop="24px" dataHook="paid-features-title">
          <Text size="small">
            {t('marketplace.template-page.features-paid-apps-indicator')}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default Features;
